import { ChevronDown, ChevronRight, Globe, Plus, Search, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { cn, DropdownPrimitive as M } from "@hostdock/ui";

/**
 * Building blocks of the Panel's list pages (Websites, Domains, …), so every page shares one header card, stat card,
 * toolbar and help card design.
 */

/** Page title card: icon tile, title, description and the page's primary action. */
export function PageHero({ icon: Icon, title, description, action }: { icon: LucideIcon; title: string; description: ReactNode; action?: ReactNode }) {
  return (
    <section className="hd-card hd-welcome flex flex-wrap items-center gap-x-5 gap-y-4 px-[16px] py-[12px] sm:min-h-[80px]">
      <span className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-2xl bg-[#ccedf2] text-[#0a8ea0] max-sm:hidden dark:bg-accent-soft dark:text-accent">
        <Icon size={29} strokeWidth={1.6} />
      </span>
      <div className="min-w-0 flex-1 basis-[15rem] leading-tight">
        <h1 className="truncate text-[24px] font-bold tracking-[-0.015em] text-fg">{title}</h1>
        <div className="mt-[4px] text-[13px] text-muted">{description}</div>
      </div>
      {action}
    </section>
  );
}

/** Solid teal call-to-action, as a link or a button. */
export const primaryButtonClass = "inline-flex h-[38px] shrink-0 items-center gap-[10px] rounded-lg bg-[#048d9f] px-[18px] text-[13px] font-semibold text-white shadow-[0_3px_10px_rgb(4_141_159/0.3)] transition hover:bg-[#037d8d] disabled:cursor-not-allowed disabled:opacity-60";

/** Outlined secondary action next to a primary one. */
export const secondaryButtonClass = "inline-flex h-[38px] shrink-0 items-center gap-[8px] rounded-lg border border-line-strong bg-surface px-[16px] text-[13px] font-medium text-fg transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60";

/** Small row buttons inside tables. */
export const rowPrimaryClass = "inline-flex h-[36px] flex-1 items-center justify-center gap-[8px] rounded-lg bg-[#048d9f] px-[14px] text-[12.5px] font-medium whitespace-nowrap text-white shadow-[0_2px_8px_rgb(4_141_159/0.25)] transition hover:bg-[#037d8d] xl:flex-none xl:px-[10px]";
export const rowSecondaryClass = "inline-flex h-[36px] flex-1 items-center justify-center gap-[8px] rounded-lg border border-line-strong bg-surface px-[14px] text-[12.5px] font-medium whitespace-nowrap text-fg transition hover:bg-surface-2 xl:flex-none xl:px-[10px]";

export const STAT_TILES = {
  teal: "bg-[#d6f5f7] text-[#0a8ea0]",
  green: "bg-[#e2f8ee] text-[#14a04a]",
  blue: "bg-[#e6f0fc] text-[#2f6fd6]",
  orange: "bg-[#fdeedf] text-[#e0611f]",
} as const;

export function StatCard({ icon: Icon, tile, label, value, sub }: { icon: LucideIcon; tile: keyof typeof STAT_TILES; label: string; value: ReactNode; sub?: string }) {
  return (
    <div className="hd-card flex h-[91px] items-center gap-[18px] px-[16px]">
      <span className={cn("flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl dark:bg-surface-3", STAT_TILES[tile])}><Icon size={23} strokeWidth={1.7} /></span>
      <div className="min-w-0 leading-tight">
        <p className="truncate text-[12.5px] text-fg/85">{label}</p>
        <p className="mt-[5px] truncate text-[21px] font-bold text-fg tabular-nums">{value}</p>
        {sub && <p className="mt-[3px] truncate text-[12px] text-muted">{sub}</p>}
      </div>
    </div>
  );
}

/** Joined filter tabs ("All (3) | Online (2) | …"). */
export function FilterTabs<T extends string>({ value, onChange, items, label }: { value: T; onChange(v: T): void; items: readonly (readonly [NoInfer<T>, string])[]; label: string }) {
  return (
    <div className="flex max-w-full overflow-x-auto rounded-lg border border-line [scrollbar-width:none]" role="group" aria-label={label}>
      {items.map(([v, text], i) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn("h-[33px] shrink-0 px-[16px] text-[12px] whitespace-nowrap transition-colors", i > 0 && "border-l border-line", value === v ? "bg-[#d9f5f9] font-medium text-[#0a8ea0] dark:bg-accent-soft dark:text-accent" : "bg-surface text-fg/85 hover:bg-surface-2")}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange(v: string): void; placeholder: string }) {
  return (
    <label className="relative block min-w-48 flex-1 sm:max-w-[331px]">
      <Search size={15} className="pointer-events-none absolute top-1/2 left-[14px] -translate-y-1/2 text-fg/70" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} className="h-[35px] w-full rounded-lg border border-line bg-surface pr-3 pl-[40px] text-[12.5px] text-fg outline-none placeholder:text-muted focus:border-accent focus:ring-2 focus:ring-ring" />
    </label>
  );
}

/** The white bar that holds filters, search and view controls. */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="hd-card mt-[22px] flex flex-wrap items-center gap-[11px] px-[8px] py-[6px]">{children}</div>;
}

/** Tinted help card linking to a page or an external document. */
export function InfoCard({ to, external, icon: Icon, tile, className, title, body }: { to: string; external?: boolean; icon: LucideIcon; tile: string; className?: string; title: string; body: string }) {
  const inner = (
    <>
      <span className={cn("flex h-[46px] w-[46px] shrink-0 items-center justify-center self-start rounded-xl dark:bg-surface-3", tile)}><Icon size={23} strokeWidth={1.7} /></span>
      <span className="min-w-0 flex-1 leading-snug">
        <span className="block text-[14.5px] font-bold text-fg">{title}</span>
        <span className="mt-[6px] block text-[12.5px] leading-[18px] text-muted">{body}</span>
      </span>
      <ChevronRight size={20} className="shrink-0 text-fg/80 transition-transform group-hover:translate-x-0.5" />
    </>
  );
  const cls = cn("group flex items-center gap-[20px] rounded-xl px-[14px] py-[16px] transition-colors hover:brightness-[0.98]", className);
  return external ? <a href={to} target="_blank" rel="noreferrer" className={cls}>{inner}</a> : <Link to={to} className={cls}>{inner}</Link>;
}

export function InfoRow({ children }: { children: ReactNode }) {
  return <div className="hd-card mt-[19px] grid gap-[19px] p-[10px] md:grid-cols-2">{children}</div>;
}

/** Pill used in tables for a status ("Secure", "Active", …). */
export const PILL_TONES = {
  ok: "bg-[#d9f3e6] text-[#14a04a] dark:bg-success-soft",
  wait: "bg-warning-soft text-warning",
  bad: "bg-danger-soft text-danger",
  off: "bg-surface-3 text-muted",
  info: "bg-[#e6f0fc] text-[#2f6fd6] dark:bg-info-soft dark:text-info",
} as const;

export function Pill({ tone, icon: Icon, dot, children }: { tone: keyof typeof PILL_TONES; icon?: LucideIcon; dot?: boolean; children: ReactNode }) {
  return (
    <span className={cn("inline-flex h-[27px] items-center gap-[7px] rounded-lg px-[11px] text-[12.5px] font-medium whitespace-nowrap", dot && "rounded-full", PILL_TONES[tone])}>
      {Icon && <Icon size={14} strokeWidth={2} />}
      {dot && <span className="h-[7px] w-[7px] rounded-full bg-current" />}
      {children}
    </span>
  );
}

/** A table cell that carries its own label when rows stack as cards on smaller screens. */
export function Cell({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div role="cell" className={cn("flex min-w-0 flex-col items-start gap-1", className)}>
      <span className="text-[10px] tracking-wide text-subtle uppercase xl:hidden">{label}</span>
      {children}
    </div>
  );
}

export const tableCell = "xl:flex xl:h-[48px] xl:items-start xl:justify-center xl:border-l xl:border-line xl:pl-[17px]";
/** The last cell of a row, holding its buttons. */
export const actionsCell = "flex items-center gap-[10px] xl:h-[48px] xl:gap-[8px] xl:border-l xl:border-line xl:pl-[10px]";

/** Table wrapper and header row: columns come from `cols` (a grid template active at xl). */
export function TableShell({ label, cols, headers, flat, children }: { label: string; cols: string; headers: string[]; /** Inside another card (e.g. a SectionCard). */ flat?: boolean; children: ReactNode }) {
  return (
    <div className={flat ? undefined : "hd-card px-[18px] pb-[3px]"} role="table" aria-label={label}>
      <div className={cn(cols, "hidden h-[40px] border-b border-line text-[9.5px] font-medium tracking-[0.06em] text-muted uppercase")} role="row">
        {headers.map((h, i) => <span key={h || i} role="columnheader" className={i ? "pl-[17px]" : undefined}>{h}</span>)}
      </div>
      <ul className="divide-y divide-line" role="rowgroup">{children}</ul>
    </div>
  );
}

/** Centered empty / no-match state inside a card. */
export function EmptyCard({ icon: Icon, title, body, action }: { icon?: LucideIcon; title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="hd-card flex flex-col items-center px-6 py-12 text-center">
      {Icon && <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent"><Icon size={24} /></span>}
      <p className="text-[14px] font-semibold">{title}</p>
      {body && <p className="mt-1 max-w-md text-[12.5px] text-muted">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Primary action for things that live inside a website (domains, databases…): goes straight to the website's tab when
 * there is one website, asks which one when there are several, and offers to create one when there are none.
 */
export function PickWebsiteButton({ label, question, tab, sites, error }: { label: string; question: string; tab: string; sites: { id: string; primaryDomain: string; status: string }[] | undefined; error?: unknown }) {
  const navigate = useNavigate();
  // Not hidden when the list failed to load: the action stays visible and says why it cannot be used right now.
  if (error) return <button type="button" disabled title="Your websites could not be loaded. Reload the page to try again." className={cn(primaryButtonClass, "cursor-not-allowed opacity-60")}><Plus size={17} strokeWidth={2.2} />{label}</button>;
  if (!sites) return null;
  const live = sites.filter((w) => w.status !== "DELETED");
  if (live.length === 0) return <Link to="/websites/new" className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />Create a website first</Link>;
  if (live.length === 1) return <Link to={`/websites/${live[0]!.id}?tab=${tab}`} className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />{label}</Link>;
  return (
    <M.Root>
      <M.Trigger asChild>
        <button type="button" className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />{label}<ChevronDown size={15} className="-mr-1" /></button>
      </M.Trigger>
      <M.Portal>
        <M.Content align="end" sideOffset={6} className="z-50 max-h-80 min-w-60 overflow-y-auto rounded-xl border border-line bg-surface p-1.5 shadow-lg">
          <M.Label className="px-2.5 py-1.5 text-[11px] font-medium tracking-wide text-subtle uppercase">{question}</M.Label>
          {live.map((w) => (
            <M.Item key={w.id} onSelect={() => navigate(`/websites/${w.id}?tab=${tab}`)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] outline-none select-none data-[highlighted]:bg-surface-2">
              <Globe size={14} className="text-subtle" />{w.primaryDomain}
            </M.Item>
          ))}
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}

/** A titled block of a detail page (e.g. "Mailboxes"), with an optional action on the right. */
export function SectionCard({ title, count, description, action, children }: { title: string; count?: number; description?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="hd-card mt-[15px]" aria-label={title}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-[18px] py-[13px]">
        <div className="min-w-0 flex-1 basis-[16rem] leading-tight">
          <h2 className="flex items-center gap-2 text-[14.5px] font-bold text-fg">{title}{count != null && <span className="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] font-semibold text-muted tabular-nums">{count}</span>}</h2>
          {description && <p className="mt-[5px] text-[12.5px] leading-[18px] text-muted">{description}</p>}
        </div>
        {action}
      </div>
      <div className="px-[18px] pb-[3px]">{children}</div>
    </section>
  );
}

/** Banner for a state the customer should act on (amber) or that blocks them (red), or plain information (teal). */
export function Banner({ tone = "warn", icon: Icon, title, children, action, className }: { tone?: "warn" | "bad" | "info"; icon: LucideIcon; title: string; children?: ReactNode; action?: ReactNode; className?: string }) {
  const box = { warn: "border-[#f9dcc0] bg-[#fff8f0] dark:border-warning/30 dark:bg-warning-soft", bad: "border-danger/30 bg-danger-soft", info: "border-[#cdeef2] bg-[#effafb] dark:border-accent/30 dark:bg-accent-soft" }[tone];
  const ic = { warn: "text-[#e0611f]", bad: "text-danger", info: "text-[#0a8ea0] dark:text-accent" }[tone];
  return (
    <div role="status" className={cn("mt-[10px] flex flex-wrap items-start gap-3 rounded-xl border p-4 text-[12.5px]", box, className)}>
      <Icon size={22} className={cn("shrink-0", ic)} />
      <div className="min-w-0 flex-1 basis-[16rem]">
        <p className="text-[13px] font-semibold text-fg">{title}</p>
        {children && <div className="mt-1 text-muted">{children}</div>}
      </div>
      {action}
    </div>
  );
}

/** A titled card for detail and wizard pages (the Panel design of `@hostdock/ui`, in the list-page style). */
export function DetailCard({ title, description, actions, flush, className, children }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; flush?: boolean; className?: string; children: ReactNode }) {
  return (
    <section className={cn("hd-card overflow-hidden", className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-[18px] py-[13px]">
          <div className="min-w-0 flex-1 basis-[16rem] leading-tight">
            {title && <h2 className="text-[14.5px] font-bold text-fg">{title}</h2>}
            {description && <p className="mt-[5px] text-[12.5px] leading-[18px] text-muted">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={flush ? undefined : "px-[18px] py-[16px]"}>{children}</div>
    </section>
  );
}
